import { useCallback, useEffect, useRef, useState } from "react";
import RemovedAttachmentsPanel from "@/components/RemovedAttachmentsPanel";
import axios from "axios";
import { Camera, Download, DownloadCloud, MapPin, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { saveBlob, saveAuthenticatedDownload } from "@/native/files";
import { useAuth } from "@/auth/AuthContext";
import { formatBusinessDate, formatTimestamp } from "@/utils/datetime";
import { PHOTO_ACCEPT, uploadPhotosToProject } from "@/utils/photoUploads";
import { formatGPS, gpsSourceLabel, hasPhotoGPS } from "@/utils/photoGps";
import ProjectPhotoLocationMap from "@/components/ProjectPhotoLocationMap";
import ProjectOriginalPhotoPreview from "@/components/ProjectOriginalPhotoPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;


// Compact Photos pill for the Project Detail header. Collapsed by default;
// tapping expands an inline panel below with the grid, upload, and download.
export default function ProjectPhotosPanel({ projectId, defaultOpen = false }) {
  const { isFeatureEnabled, hasPermission } = useAuth();
  const enabled = isFeatureEnabled("project_photos");
  const gpsEnabled = enabled && isFeatureEnabled("photo_gps_map");
  const canRead = hasPermission("project_photos:read");
  const canManage = hasPermission("project_photos:manage");
  const [removedVersion, setRemovedVersion] = useState(0);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(defaultOpen);
  const [photos, setPhotos] = useState([]);
  const [photosProjectId, setPhotosProjectId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [selected, setSelected] = useState(null);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [gpsOnly, setGpsOnly] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [activePhotoId, setActivePhotoId] = useState(null);
  const [mapFocusRequest, setMapFocusRequest] = useState(0);
  const [gpsCandidates, setGpsCandidates] = useState({});
  const [gpsScanBusy, setGpsScanBusy] = useState(false);
  const [gpsConfirming, setGpsConfirming] = useState(null);
  const [utmZones, setUtmZones] = useState({});
  const [utmPreviewBusy, setUtmPreviewBusy] = useState(null);
  const [storageCheck, setStorageCheck] = useState(null);
  const [storageCheckBusy, setStorageCheckBusy] = useState(false);
  const [brokenThumbIds, setBrokenThumbIds] = useState(() => new Set());
  const gpsCount = photos.filter(hasPhotoGPS).length;
  const visiblePhotos = gpsEnabled && gpsOnly ? photos.filter(hasPhotoGPS) : photos;
  const inputRef = useRef(null);
  const lastThumbRefresh = useRef(0);
  const scanLock = useRef(false);
  const scanGeneration = useRef(0);
  const listGeneration = useRef(0);
  const gpsGeneration = useRef(0);
  const [gpsRefresh, setGpsRefresh] = useState(0);
  const [gpsStatusError, setGpsStatusError] = useState(false);
  const scannedCount = photos.filter(p => gpsCandidates[p.id] && !["scanning", "queued", "not_queued"].includes(gpsCandidates[p.id].status)).length;
  const candidateCount = photos.filter(p => gpsCandidates[p.id]?.status === "candidate").length;
  const failedScans = photos.filter(p => !hasPhotoGPS(p) && gpsCandidates[p.id]?.status === "unavailable");

  useEffect(() => {
    if (!selected) return undefined;
    const dismiss = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      // The expanded original owns the first Back/Escape action.
      if (document.querySelector('[data-testid="project-photo-expanded-original"]')) return;
      event.preventDefault();
      setSelected(null);
    };
    document.addEventListener("keydown", dismiss);
    return () => document.removeEventListener("keydown", dismiss);
  }, [selected]);

  const toggleChecked = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setCheckedIds((prev) => {
      if (prev.size === photos.length) return new Set();
      return new Set(photos.map((p) => p.id));
    });
  };

  const downloadIds = async (idsIterable, isAll) => {
    const ids = Array.from(idsIterable);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/photos/bulk-zip`,
        { photo_ids: ids },
        { responseType: "blob" },
      );
      const missing = Number(res.headers?.["x-missing-count"] || 0);
      const included = Number(res.headers?.["x-included-count"] || 0);
      const delivery = await saveBlob(res.data, `project-${projectId}-photos.zip`);
      if (delivery.status === "cancelled") return;
      if (!isAll) setCheckedIds(new Set());
      if (missing > 0) {
        toast.warning(
          `${included} photo${included === 1 ? "" : "s"} downloaded — ${missing} skipped (storage lookup failed).`,
        );
      }
    } catch (error) {
      let detail = "ZIP download failed.";
      const body = error?.response?.data;
      if (body instanceof Blob) {
        try {
          const asText = await body.text();
          const parsed = JSON.parse(asText);
          if (parsed?.detail) detail = parsed.detail;
        } catch {
          // fall through with the generic message
        }
      } else if (body?.detail) {
        detail = body.detail;
      }
      toast.error(detail);
    }
  };

  const downloadZip = () => downloadIds(checkedIds, false);
  const downloadAll = () => downloadIds(photos.map((p) => p.id), true);

  const loadCount = useCallback(async () => {
    if (!enabled || !canRead) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/photos/count`);
      setCount(res.data.count || 0);
    } catch (error) {
      // Silently ignore — the pill just won't show a number.
    }
  }, [enabled, canRead, projectId]);

  const loadList = useCallback(async () => {
    if (!enabled || !canRead) return;
    const generation = scanGeneration.current;
    const request = ++listGeneration.current;
    const current = () => generation === scanGeneration.current && request === listGeneration.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/photos`);
      if (!current()) return;
      const rows = res.data.photos || [];
      setPhotos(rows);
      setSelected(previous => previous ? rows.find(photo => photo.id === previous.id) || previous : null);
      setPhotosProjectId(projectId);
      // Leave failed thumbnail indicators visible until a user explicitly
      // retries. A missing R2/local file can never be fixed by re-signing URLs.
      setCount(rows.length);
    } catch (error) {
      if (current()) toast.error(error?.response?.data?.detail || "Could not load photos.");
    } finally {
      if (current()) setLoading(false);
    }
  }, [enabled, canRead, projectId]);

  useEffect(() => { loadCount(); }, [loadCount]);
  useEffect(() => {
    setGpsOnly(false);
    setMapLoaded(false);
    setActivePhotoId(null);
    setSelected(null);
    setGpsCandidates({});
    setUtmZones({});
    setUtmPreviewBusy(null);
    scanGeneration.current += 1;
    setGpsStatusError(false);
    setPhotosProjectId(null);
    setPhotos([]);
    setUploading(false);
    setUploadProgress([]);
    setGpsConfirming(null);
    scanLock.current = false;
    setGpsScanBusy(false);
    setStorageCheck(null);
    setBrokenThumbIds(new Set());
    return () => { scanGeneration.current += 1; };
  }, [projectId]);
  // Refresh on enable so retained server coordinates return to the gallery.
  useEffect(() => { if (open) loadList(); }, [open, loadList, gpsEnabled]);
  useEffect(() => {
    gpsGeneration.current += 1;
    if (!gpsEnabled) {
      setGpsOnly(false);
      setMapLoaded(false);
      setActivePhotoId(null);
      setGpsCandidates({});
      setGpsStatusError(false);
      setGpsScanBusy(false);
      setGpsConfirming(null);
      scanLock.current = false;
    }
    return () => { gpsGeneration.current += 1; };
  }, [gpsEnabled, projectId]);

  const refreshExpiredPhotoUrls = () => {
    // Short-lived signed thumbnails can expire in an open gallery.
    // Refresh once per minute rather than trigger 150+ list requests.
    const now = Date.now();
    if (now - lastThumbRefresh.current < 60_000) return;
    lastThumbRefresh.current = now;
    loadList();
  };

  const checkPhotoStorage = async () => {
    if (!enabled || !canRead || storageCheckBusy) return;
    setStorageCheckBusy(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/photos/storage-check`);
      setStorageCheck(res.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not check photo storage.");
    } finally {
      setStorageCheckBusy(false);
    }
  };

  const photoBroken = (photo) => {
    setBrokenThumbIds(prev => new Set(prev).add(photo.id));
    refreshExpiredPhotoUrls();
  };

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const generation = scanGeneration.current;
    setUploading(true);
    setUploadProgress(files.map((f) => ({ name: f.name, progress: 0 })));
    try {
      await uploadPhotosToProject(projectId, files, (index, progress) => {
        setUploadProgress((prev) => {
          if (!prev[index]) return prev;
          const next = prev.slice();
          next[index] = { ...next[index], progress };
          return next;
        });
      });
      if (generation !== scanGeneration.current) return;
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}.`);
    } catch (error) {
      if (generation !== scanGeneration.current) return;
      toast.error(error?.failedCount
        ? `${error.uploadedCount} uploaded; ${error.failedCount} uploads could not be confirmed. Check the refreshed photo list before retrying.`
        : error?.response?.data?.detail || "Upload failed.");
    } finally {
      if (generation === scanGeneration.current) {
        // All upload workers have settled, including partial failure. Pick up
        // every saved photo so automatic GPS also handles successful siblings.
        await loadList();
        setUploading(false);
        setUploadProgress([]);
        if (inputRef.current) inputRef.current.value = "";
      }
    }
  };

  // Only status polling belongs to the page. The durable server queue owns work.
  useEffect(() => {
    if (!open || !gpsEnabled || !canRead || !canManage || loading || uploading || photosProjectId !== projectId) return undefined;
    let stopped = false;
    let timer;
    const generation = scanGeneration.current;
    const current = () => !stopped && generation === scanGeneration.current;
    const sync = async (enqueue) => {
      try {
        const url = `${API}/projects/${projectId}/photos/gps-jobs`;
        // Idempotent: cached candidates/no-result/failures are never requeued here.
        const res = enqueue ? await axios.post(url) : await axios.get(url);
        if (!current()) return;
        const results = Object.fromEntries((res.data.results || []).map(({ photo_id, ...result }) => [photo_id, result]));
        setGpsCandidates(results);
        setGpsStatusError(false);
        const pending = Object.values(results).some(result => ["queued", "scanning"].includes(result.status));
        setGpsScanBusy(pending);
        if (pending) timer = setTimeout(() => sync(false), 3000);
      } catch {
        if (!current()) return;
        setGpsStatusError(true);
        setGpsScanBusy(false);
      }
    };
    sync(true);
    return () => { stopped = true; clearTimeout(timer); };
  }, [open, gpsEnabled, canRead, canManage, loading, uploading, photosProjectId, projectId, photos, gpsRefresh]);

  const runGPSScan = async (photosToScan) => {
    if (!gpsEnabled || scanLock.current || gpsConfirming || !canManage) return;
    const generation = gpsGeneration.current;
    scanLock.current = true;
    try {
      for (const photo of photosToScan.filter(p => !hasPhotoGPS(p)).slice(0, 5)) {
        await axios.post(`${API}/projects/${projectId}/photos/${photo.id}/gps-scan`);
        if (generation !== gpsGeneration.current) return;
      }
    } catch (error) {
      if (generation === gpsGeneration.current) toast.error(error?.response?.data?.detail || "Could not retry location checking.");
    } finally {
      if (generation === gpsGeneration.current) {
        scanLock.current = false;
        setGpsRefresh(value => value + 1);
      }
    }
  };

  const confirmGPS = async (photo, candidate) => {
    if (!gpsEnabled || !canManage || !candidate || candidate.status !== "candidate" || gpsConfirming) return;
    const generation = gpsGeneration.current;
    setGpsConfirming(photo.id);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/photos/${photo.id}/gps-confirm`,
        {
          gps_latitude: candidate.gps_latitude,
          gps_longitude: candidate.gps_longitude,
          gps_source: candidate.source,
          sha256: candidate.sha256,
        },
      );
      if (generation !== gpsGeneration.current) return;
      setGpsCandidates(prev => {
        const next = { ...prev };
        delete next[photo.id];
        return next;
      });
      setSelected(prev => prev?.id === photo.id ? res.data : prev);
      await loadList();
      toast.success("Confirmed photo GPS saved. Photo is now on the map.");
    } catch (error) {
      if (generation === gpsGeneration.current) toast.error(error?.response?.data?.detail || "Could not confirm GPS. Rescan this photo.");
    } finally {
      if (generation === gpsGeneration.current) setGpsConfirming(null);
    }
  };

  const previewUTM = async (photo, candidate) => {
    if (!gpsEnabled || !canManage || utmPreviewBusy || gpsConfirming) return;
    const raw = (utmZones[photo.id] || "").trim().toUpperCase();
    const zone = raw.match(/^([1-9]|[1-5]\d|60)([NS])$/);
    if (!zone) {
      toast.error("Enter the verified UTM zone and hemisphere, such as 14N.");
      return;
    }
    setUtmPreviewBusy(photo.id);
    try {
      const response = await axios.post(
        API + "/projects/" + projectId + "/photos/" + photo.id + "/gps-utm-preview",
        { zone: Number(zone[1]), hemisphere: zone[2], sha256: candidate.sha256 },
      );
      setGpsCandidates(previous => ({ ...previous, [photo.id]: response.data }));
      toast.success("Converted location ready for review. No map pin has been saved.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not preview UTM location.");
    } finally {
      setUtmPreviewBusy(null);
    }
  };

  const candidateLabel = (candidate) => (
    candidate?.source === "exif_recovered" ? "Recovered original EXIF GPS" : "Coordinates printed on photo (OCR)"
  );

  const candidateView = (photo) => {
    if (!gpsEnabled) return null;
    const candidate = gpsCandidates[photo.id];
    if (!candidate) return null;
    if (["scanning", "queued"].includes(candidate.status)) return <p role="status">{candidate.message || "Finding photo location…"} You can leave this page after uploading.</p>;
    if (candidate.status === "not_queued") return null;
    if (candidate.status === "utm_zone_required") return (
      <div className="photo-gps-map__candidate" data-testid={"gps-utm-review-" + photo.id}>
        <strong>UTM coordinates found — zone required</strong>
        <div>Easting: {candidate.utm_easting} · Northing: {candidate.utm_northing}</div>
        <small>Inspect the original photo and verify the missing UTM zone from a reliable source. The grid numbers alone do not determine a location.</small>
        <label>Verified UTM zone and hemisphere
          <input type="text" placeholder="e.g. 14N" value={utmZones[photo.id] || ""}
            data-testid={"gps-utm-zone-" + photo.id}
            onChange={event => setUtmZones(previous => ({ ...previous, [photo.id]: event.target.value }))}
            maxLength={3} />
        </label>
        <button type="button" disabled={Boolean(utmPreviewBusy || gpsConfirming)}
          data-testid={"gps-utm-preview-" + photo.id}
          onClick={() => previewUTM(photo, candidate)}>
          {utmPreviewBusy === photo.id ? "Converting…" : "Preview location (do not save pin)"}
        </button>
      </div>
    );
    if (candidate.status !== "candidate") return (
      <div><p role="status">{candidate.message || (candidate.status === "existing"
        ? "Stored GPS already exists; OCR was skipped."
        : "No reliable coordinates found. Original photo was not changed.")}</p>
        {["unavailable", "no_candidate"].includes(candidate.status) && (
          <button type="button" data-testid={`gps-retry-${photo.id}`}
            disabled={gpsScanBusy || Boolean(gpsConfirming)} onClick={() => runGPSScan([photo])}>
            Retry scan
          </button>
        )}
      </div>
    );
    return (
      <div className="photo-gps-map__candidate" data-testid={`gps-candidate-${photo.id}`}>
        <strong>{candidateLabel(candidate)}</strong>
        <div>{candidate.gps_latitude}, {candidate.gps_longitude}</div>
        <small>Please verify these coordinates against the photo before saving.</small>
        <a href={"https://www.openstreetmap.org/?mlat=" + candidate.gps_latitude + "&mlon=" + candidate.gps_longitude}
          target="_blank" rel="noopener noreferrer">Preview location on OpenStreetMap</a>
        <button type="button" disabled={Boolean(gpsConfirming)}
          data-testid={`gps-confirm-${photo.id}`}
          onClick={() => confirmGPS(photo, candidate)}>
          {gpsConfirming === photo.id ? "Saving…" : "Confirm & add map pin"}
        </button>
        <button type="button" disabled={gpsScanBusy || Boolean(gpsConfirming)}
          onClick={() => runGPSScan([photo])}>Rescan photo</button>
      </div>
    );
  };

  const remove = async (photo) => {
    if (!window.confirm(`Remove ${photo.original_filename}? It stays recoverable for 30 days.`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/photos/${photo.id}`);
      toast.success("Photo removed.");
      setRemovedVersion(value => value + 1);
      setSelected(null);
      await loadList();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Delete failed.");
    }
  };

  if (!enabled || !canRead) return null;

  return (
    <div className="project-photos" data-testid="project-photos-root" style={{ width: "100%", minWidth: 0 }}>
      <button
        className="table-edit-button"
        data-testid="project-photos-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
        type="button"
      >
        <Camera size={14} />
        Photos ({count})
      </button>

      {open && (
        <section
          data-testid="project-photos-panel"
          style={{
            marginTop: "0.65rem",
            padding: "0.75rem",
            width: "100%",
            boxSizing: "border-box",
            border: "1px solid #e2e5ea",
            borderRadius: "8px",
            background: "#fafbfc",
          }}
        >
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.6rem", flexWrap: "wrap" }}>
            {canManage && (
              <>
                <input
                  accept="image/*"
                  data-testid="project-photos-input"
                  multiple
                  onChange={upload}
                  ref={inputRef}
                  style={{ display: "none" }}
                  type="file"
                />
                <button
                  className="table-edit-button"
                  data-testid="project-photos-upload"
                  disabled={uploading}
                  onClick={() => inputRef.current?.click()}
                  type="button"
                >
                  <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
                </button>
              </>
            )}
            {photos.length > 0 && (
              <>
                <button
                  className="table-edit-button"
                  data-testid="project-photos-select-all"
                  onClick={toggleSelectAll}
                  type="button"
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                >
                  {checkedIds.size === photos.length ? "Clear selection" : `Select all (${photos.length})`}
                </button>
                <button
                  className="table-edit-button"
                  data-testid="project-photos-download-all"
                  onClick={downloadAll}
                  type="button"
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                >
                  <DownloadCloud size={13} /> Download all ({photos.length})
                </button>
              </>
            )}
            {checkedIds.size > 0 && checkedIds.size !== photos.length && (
              <button
                className="table-edit-button"
                data-testid="project-photos-download-zip"
                onClick={downloadZip}
                type="button"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
              >
                <DownloadCloud size={13} /> Download selected ({checkedIds.size})
              </button>
            )}
            <button
              data-testid="project-photos-close"
              onClick={() => setOpen(false)}
              style={{ marginLeft: "auto" }}
              type="button"
            >
              Close
            </button>
          </div>

          {!loading && photos.length > 0 && (
            <div className="photo-gps-map__controls">
              {gpsEnabled && <><label data-testid="project-photos-gps-filter">
                <input type="checkbox" checked={gpsOnly} onChange={event => setGpsOnly(event.target.checked)} />
                GPS only ({gpsCount})
              </label>
              <span>{photos.length - gpsCount} without stored GPS</span></>}
              <button type="button" className="table-edit-button"
                onClick={checkPhotoStorage} disabled={storageCheckBusy}
                data-testid="project-photo-check-storage">
                {storageCheckBusy ? "Checking…" : "Check photo storage"}
              </button>
              {brokenThumbIds.size > 0 && (
                <button type="button" className="table-edit-button"
                  data-testid="project-photo-retry-images"
                  onClick={() => { setBrokenThumbIds(new Set()); loadList(); }}>
                  Retry images ({brokenThumbIds.size})
                </button>
              )}
              {gpsEnabled && canManage && gpsScanBusy && <span role="status">Finding photo locations in the background…</span>}
              {gpsEnabled && canManage && gpsStatusError && <button type="button" onClick={() => setGpsRefresh(value => value + 1)}>
                Could not refresh location status. Retry
              </button>}
              {gpsEnabled && canManage && failedScans.length > 0 && (
                <button type="button" className="table-edit-button" data-testid="gps-retry-failed"
                  disabled={gpsScanBusy || Boolean(gpsConfirming)} onClick={() => runGPSScan(failedScans)}>
                  Retry failed scans (up to 5)
                </button>
              )}
              {gpsEnabled && scannedCount > 0 && <span role="status">Scanned {scannedCount} · {candidateCount} ready to confirm · {failedScans.length} failed</span>}
              {gpsEnabled && <span>Photos are checked automatically after upload, even when you leave this page. Results are saved for your next visit. Coordinates read from the picture need confirmation.</span>}
            </div>
          )}
          {storageCheck && (
            <div className="photo-storage-diagnostics" data-testid="project-photo-storage-results"
              role="status">
              <strong>Photo storage: {storageCheck.storage_backend === "local"
                ? "temporary local container" : "Cloudflare R2"}</strong>
              <span>Checked {storageCheck.checked} photo records (read only).</span>
              <span>Originals available: {storageCheck.items.filter(p => p.original === "available").length};
                missing: {storageCheck.items.filter(p => p.original === "missing").length};
                unavailable: {storageCheck.items.filter(p => p.original === "unavailable").length}.</span>
              <span>Thumbnails available: {storageCheck.items.filter(p => p.thumbnail === "available").length};
                missing: {storageCheck.items.filter(p => p.thumbnail === "missing").length};
                unavailable: {storageCheck.items.filter(p => p.thumbnail === "unavailable").length}.</span>
              <div className="photo-storage-diagnostics__items">
                {storageCheck.items.map(item => {
                  const photo = photos.find(p => p.id === item.photo_id);
                  return <div key={item.photo_id} className="photo-storage-diagnostics__item">
                    <span>{photo?.original_filename || "Photo"}</span>
                    <span>Original: {item.original} · Thumbnail: {item.thumbnail}</span>
                  </div>;
                })}
              </div>
              {storageCheck.storage_backend === "local" && (
                <strong className="photo-storage-diagnostics__warning">
                  Warning: local container files may disappear after redeployment.
                  Check R2 configuration before uploading more job photos.
                </strong>
              )}
            </div>
          )}
          {gpsEnabled && canManage && Object.keys(gpsCandidates).length > 0 && (
            <div className="photo-gps-map__candidate-list" data-testid="project-photos-gps-results">
              <strong>Location results — map pins need confirmation</strong>
              {photos.filter(p => gpsCandidates[p.id] && p.id !== selected?.id).map(photo => (
                <div key={photo.id} className="photo-gps-map__candidate-row">
                  <div className="photo-gps-map__candidate-photo">
                    <button type="button" title="View the original photo and verify GPS"
                      aria-label={`View original ${photo.original_filename}`}
                      data-testid={`gps-review-photo-${photo.id}`}
                      onClick={() => setSelected(photo)}>
                      <img src={photo.thumbnail_url} alt={photo.original_filename}
                        onError={refreshExpiredPhotoUrls} />
                    </button>
                    <div>
                      <strong>{photo.original_filename}</strong>
                      <button className="photo-gps-map__review-open" type="button"
                        onClick={() => setSelected(photo)}>
                        View photo before confirming
                      </button>
                    </div>
                  </div>
                  {candidateView(photo)}
                </div>
              ))}
            </div>
          )}
          <div className={`project-photo-layout${gpsEnabled ? "" : " project-photo-layout--gallery-only"}`}>
            <div className="project-photo-gallery">
          {loading ? (
            <p data-testid="project-photos-loading">Loading…</p>
          ) : uploadProgress.length > 0 ? (
            <ul
              data-testid="project-photos-upload-progress"
              style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "0.35rem" }}
            >
              {uploadProgress.map((item, index) => (
                <li
                  data-testid={`project-photos-upload-progress-${index}`}
                  key={`${item.name}-${index}`}
                  style={{ fontSize: "0.8rem", color: "#374151" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                    <span
                      style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={item.name}
                    >
                      {item.name}
                    </span>
                    <span>{Math.round(item.progress * 100)}%</span>
                  </div>
                  <div
                    style={{
                      background: "#e5e7eb",
                      height: 4,
                      borderRadius: 2,
                      overflow: "hidden",
                      marginTop: 2,
                    }}
                  >
                    <div
                      style={{
                        background: item.progress >= 1 ? "#16a34a" : "#2563eb",
                        height: "100%",
                        width: `${Math.min(100, item.progress * 100)}%`,
                        transition: "width 120ms linear",
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : photos.length === 0 ? (
            <p data-testid="project-photos-empty" style={{ color: "#64748b" }}>
              No photos yet.
            </p>
          ) : (
            <div
              data-testid="project-photos-grid"
              style={{
                display: "grid",
                gap: "0.5rem",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              }}
            >
              {visiblePhotos.map((photo) => {
                const isChecked = checkedIds.has(photo.id);
                return (
                  <div
                    key={photo.id}
                    data-testid={`project-photo-tile-${photo.id}`}
                    style={{
                      position: "relative",
                      border: isChecked || activePhotoId === photo.id ? "2px solid #2563eb" : "1px solid #d1d5db",
                      borderRadius: "6px",
                      overflow: "hidden",
                      background: "#fff",
                      aspectRatio: "1 / 1",
                    }}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select ${photo.original_filename}`}
                      data-testid={`project-photo-check-${photo.id}`}
                      checked={isChecked}
                      onChange={() => toggleChecked(photo.id)}
                      style={{
                        position: "absolute",
                        top: 6,
                        left: 6,
                        zIndex: 2,
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                    <button
                      data-testid={`project-photo-thumb-${photo.id}`}
                      onClick={() => { setSelected(photo); setActivePhotoId(photo.id); }}
                      className={activePhotoId === photo.id ? "gps-active" : ""}
                      style={{
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        width: "100%",
                        height: "100%",
                      }}
                      title={photo.original_filename}
                      type="button"
                    >
                      {brokenThumbIds.has(photo.id) ? (
                        <span className="photo-missing-thumbnail" data-testid={`missing-photo-${photo.id}`}>
                          Image unavailable
                          <small>Open to check original</small>
                        </span>
                      ) : (
                        <img
                          alt={photo.original_filename}
                          src={photo.thumbnail_url}
                          onError={() => photoBroken(photo)}
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                      )}
                    </button>
                    {gpsEnabled && hasPhotoGPS(photo) && (
                      <button className="photo-gps-map__tile-location"
                        type="button" title={gpsSourceLabel(photo)}
                        data-testid={`project-photo-gps-focus-${photo.id}`}
                        onClick={() => { setMapLoaded(true); setActivePhotoId(photo.id); setMapFocusRequest(n => n + 1); }}
                        style={{ position: "absolute", bottom: 5, right: 5, zIndex: 2,
                          borderRadius: 4, border: "1px solid white", background: "#083e88",
                          color: "white", display: "flex", alignItems: "center", gap: 2,
                          fontSize: 10, padding: "3px 5px" }}>
                        <MapPin size={11} /> GPS
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
            </div>
            {gpsEnabled && <aside className="project-photo-gps-side" data-testid="project-photos-gps-side">
              {mapLoaded || gpsCount === 0 ? (
                <ProjectPhotoLocationMap photos={photos} activeId={activePhotoId}
                  focusRequestId={mapFocusRequest}
                  onSelectPhoto={setActivePhotoId} onViewPhoto={setSelected} />
              ) : (
                <div className="photo-gps-map__consent" data-testid="project-photos-map-consent">
                  <strong>Photo GPS Map · {gpsCount} located</strong>
                  <p>Display photo locations on an interactive street map alongside this gallery.</p>
                  <p>Loading the map requests map tiles from OpenStreetMap. Your photo coordinates stay inside JFM; the tile service receives approximate map areas.</p>
                  <button type="button" onClick={() => setMapLoaded(true)}
                    data-testid="project-photos-load-map">
                    <MapPin size={13} /> Load GPS map
                  </button>
                </div>
              )}
            </aside>}
          </div>
          {canManage && <RemovedAttachmentsPanel projectId={projectId} kind="photos" onRestored={loadList} refreshKey={removedVersion} />}
        </section>
      )}

      {selected && (
        <div
          data-testid="project-photo-detail"
          role="dialog"
          aria-modal="true"
          aria-label={selected.original_filename}
          onClick={() => setSelected(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 60,
            padding: "0.5rem",
            overflowY: "auto",
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "8px",
              maxWidth: "760px",
              width: "100%",
              padding: "1rem",
              maxHeight: "calc(100dvh - 1rem)",
              overflowY: "auto",
              boxSizing: "border-box",
            }}
          >
            <h3 style={{ marginTop: 0 }}>{selected.original_filename}</h3>
            <ProjectOriginalPhotoPreview projectId={projectId} photo={selected} />
            <dl style={{ fontSize: "0.85rem", marginTop: "0.75rem" }}>
              <div><strong>Captured:</strong> {selected.captured_at ? formatTimestamp(selected.captured_at) : "—"}</div>
              <div><strong>Uploaded:</strong> {formatTimestamp(selected.uploaded_at)} by {selected.uploaded_by_name_snapshot}</div>
              {gpsEnabled && <div><strong>GPS:</strong> {formatGPS(selected)}</div>}
              {gpsEnabled && <div><strong>GPS source:</strong> {gpsSourceLabel(selected)}</div>}
              {gpsEnabled && canManage && !hasPhotoGPS(selected) && (
                <div data-testid="project-photo-gps-review">
                  {candidateView(selected)}
                </div>
              )}
              {gpsEnabled && selected.gps_altitude != null && <div><strong>Altitude:</strong> {selected.gps_altitude} m</div>}
              {gpsEnabled && selected.gps_heading != null && <div><strong>Direction:</strong> {selected.gps_heading}°</div>}
              <div><strong>Size:</strong> {(selected.byte_size / 1024).toFixed(1)} KB · {selected.content_type}</div>
              {selected.exif_summary?.Make && (
                <div>
                  <strong>Camera:</strong> {selected.exif_summary.Make} {selected.exif_summary.Model || ""}
                </div>
              )}
            </dl>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
              <a
                className="table-edit-button"
                data-testid="project-photo-download"
                download={selected.original_filename}
                onClick={(event) => {
                  event.preventDefault();
                  saveAuthenticatedDownload(`${API}/projects/${projectId}/photos/${selected.id}/download`, selected.original_filename)
                    .catch(() => toast.error("Original photo could not be downloaded."));
                }}
                href={`${API}/projects/${projectId}/photos/${selected.id}/download`}
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
              >
                <Download size={13} /> Download original
              </a>
              {gpsEnabled && hasPhotoGPS(selected) && (
                <button className="table-edit-button" type="button"
                  data-testid="project-photo-locate-on-map"
                  onClick={() => { setActivePhotoId(selected.id); setMapFocusRequest(n => n + 1); setMapLoaded(true); setSelected(null); }}>
                  <MapPin size={13} /> Locate on map
                </button>
              )}
              {canManage && (
                <button
                  className="table-edit-button"
                  data-testid="project-photo-delete"
                  onClick={() => remove(selected)}
                  style={{ color: "#dc2626" }}
                  type="button"
                >
                  <Trash2 size={13} /> Remove
                </button>
              )}
              <button
                data-testid="project-photo-close-detail"
                onClick={() => setSelected(null)}
                style={{ marginLeft: "auto" }}
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

